import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Download, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loading } from "@/components/Common";
import { SimpleSelect } from "@/components/Filters";
import { useLoad } from "@/hooks/useData";
import { api } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import { dateLabel, todayStr } from "@/lib/format";

const vendorText = (vendor, lines) => [`Pending items — ${vendor || "No vendor"} (as of ${dateLabel(todayStr())})`,
  ...lines.map((l, i) => `${i + 1}. ${l.name}${l.sku ? ` [${l.sku}]` : ""} — ${l.pending} pcs (${l.order_no}${l.due ? `, due ${dateLabel(l.due)}` : ""})`),
  `Total pending: ${lines.reduce((s, l) => s + l.pending, 0)} pcs`].join("\n");

const byKey = (rows, k) => rows.reduce((m, r) => ({ ...m, [r[k]]: [...(m[r[k]] || []), r] }), {});

const VendorSummary = ({ open, onOpenChange, rows }) => {
  const groups = byKey(rows, "supplier");
  const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copied — paste it in WhatsApp")).catch(() => toast.error("Copy failed, select the text and copy manually"));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="vendor-summary-dialog">
        <DialogHeader><DialogTitle>Pending summary for vendors</DialogTitle></DialogHeader>
        {Object.entries(groups).map(([v, lines], i) => {
          const t = vendorText(v, lines);
          return (
            <div key={v} className="rounded-md border border-border p-3" data-testid={`vendor-summary-${i}`}>
              <div className="mb-2 flex items-center justify-between gap-2"><span className="font-semibold">{v || "No vendor"}</span>
                <Button size="sm" variant="outline" onClick={() => copy(t)} data-testid={`vendor-summary-copy-${i}`}><Copy className="h-4 w-4" /> Copy</Button></div>
              <pre className="whitespace-pre-wrap break-words font-mono text-xs">{t}</pre>
            </div>);
        })}
      </DialogContent>
    </Dialog>
  );
};

const Flat = ({ rows }) => (
  <table className="data-table" data-testid="pending-table">
    <thead><tr><th>Item</th><th>PO no</th><th>Vendor</th><th>Ordered</th><th>Received</th><th>Pending</th><th>Due date</th><th>Days overdue</th></tr></thead>
    <tbody>{rows.map((r, i) => (
      <tr key={`${r.po_id}-${r.product_id}`} data-testid={`pending-row-${i}`}>
        <td className="wrap-cell min-w-[240px]"><div className="product-name">{r.name}</div>{r.sku && <div className="font-mono text-[10px] text-muted-foreground">{r.sku}</div>}</td>
        <td><Link to={`/po-receipts/po/${r.po_id}`} className="font-mono font-bold text-primary hover:underline">{r.order_no}</Link></td>
        <td className="wrap-cell text-xs">{r.supplier}</td><td className="font-mono">{r.ordered}</td><td className="font-mono">{r.received}</td>
        <td className="font-mono font-semibold text-amber-600">{r.pending}</td><td className="text-xs">{r.due ? dateLabel(r.due) : "—"}</td>
        <td className={`font-mono ${r.days_overdue ? "text-red-600 font-semibold" : ""}`}>{r.days_overdue || ""}</td>
      </tr>))}</tbody>
  </table>
);

const Grouped = ({ rows, by }) => {
  const groups = Object.entries(byKey(rows, by === "item" ? "product_id" : "supplier"));
  return (
    <table className="data-table" data-testid="pending-grouped-table">
      <thead><tr><th>{by === "item" ? "Item" : "Vendor"}</th><th>Lines</th><th>Total pending</th><th>{by === "item" ? "POs" : "Items"}</th></tr></thead>
      <tbody>{groups.map(([k, ls], i) => (
        <tr key={k} data-testid={`pending-group-${i}`}>
          <td className="wrap-cell min-w-[240px] product-name font-medium">{by === "item" ? ls[0].name : k || "No vendor"}</td><td className="font-mono">{ls.length}</td>
          <td className="font-mono font-bold text-amber-600">{ls.reduce((s, l) => s + l.pending, 0)}</td>
          <td className="wrap-cell text-xs">{ls.map((l) => `${by === "item" ? l.order_no : l.name} (${l.pending})`).join(", ")}</td>
        </tr>))}</tbody>
    </table>
  );
};

export default function PendingTab() {
  const { data } = useLoad(() => api.get("/po-receipts/pending").then((r) => r.data));
  const [by, setBy] = useState("none");
  const [sum, setSum] = useState(false);
  const rows = useMemo(() => data || [], [data]);
  if (!data) return <Loading />;
  const csv = () => downloadCsv(`pending-items-${todayStr()}.csv`, ["Item", "SKU", "PO no", "Vendor", "Ordered", "Received", "Short closed", "Pending", "Due date", "Days overdue"],
    rows.map((r) => [r.name, r.sku, r.order_no, r.supplier, r.ordered, r.received, r.short, r.pending, r.due || "", r.days_overdue]));
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <SimpleSelect value={by} onChange={setBy} testid="pending-group-by" className="h-9 w-48" options={[["none", "No grouping"], ["item", "Group by item"], ["vendor", "Group by vendor"]]} />
        <span className="font-mono text-xs text-muted-foreground" data-testid="pending-total">{rows.length} lines · {rows.reduce((s, r) => s + r.pending, 0)} pcs pending</span>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setSum(true)} disabled={!rows.length} data-testid="pending-vendor-summary-btn"><MessageSquareText className="h-4 w-4" /> Pending summary for vendor</Button>
          <Button size="sm" variant="outline" onClick={csv} disabled={!rows.length} data-testid="pending-csv-btn"><Download className="h-4 w-4" /> CSV</Button>
        </div>
      </div>
      <div className="panel overflow-auto max-h-[calc(100vh-300px)]">{by === "none" ? <Flat rows={rows} /> : <Grouped rows={rows} by={by} />}</div>
      <VendorSummary open={sum} onOpenChange={setSum} rows={rows} />
    </div>
  );
}
